'use client'

import { ArrowRight } from 'lucide-react'
import { MouseEvent, useEffect, useState } from 'react'

import legacyPreviewStyles from '@/app/fanfic-landing/fanfic-landing.module.css'
import { trackGTMEvent } from '@/utils/analytics'
import {
  faqItems,
  faqSectionContent,
  finalCtaContent,
  flowScreens,
  flowSectionContent,
  heroContent,
  heroSpeechBubbles,
} from '../_constants/content'
import {
  DEFAULT_READER_LANDING_CTA,
  getReaderLandingCta,
  isReaderLandingPlaceholderHref,
  ReaderLandingCta,
} from '../_utils/cta'
import styles from '../fanfic-reader-landing.module.css'
import { SectionLabel } from './SectionLabel'

type PreviewMode = 'thread' | 'chat'
type LandingCtaSource = 'hero_cta' | 'preview_cta' | 'final_cta'

const preferenceScreen = flowScreens[0]
const feedScreen = flowScreens[1]

function scrollToAnchor(event: MouseEvent<HTMLAnchorElement>, id: string) {
  event.preventDefault()
  document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' })
}

function trackLandingCtaClick(cta: ReaderLandingCta, source: LandingCtaSource) {
  trackGTMEvent('button_click', {
    event_category: 'conversion',
    button_name: source,
    link_url: cta.href,
    cta_state: cta.state,
  })
}

export function FanficReaderLandingPage() {
  const [previewMode, setPreviewMode] = useState<PreviewMode>('thread')
  const [pairingMode, setPairingMode] = useState<'original' | 'converted'>('original')
  const [cta, setCta] = useState<ReaderLandingCta>(DEFAULT_READER_LANDING_CTA)

  useEffect(() => {
    setCta(getReaderLandingCta())
  }, [])

  useEffect(() => {
    const timer = window.setInterval(() => {
      setPreviewMode((prev) => (prev === 'thread' ? 'chat' : 'thread'))
    }, 4200)

    return () => window.clearInterval(timer)
  }, [])

  useEffect(() => {
    const timer = window.setInterval(() => {
      setPairingMode((prev) => (prev === 'original' ? 'converted' : 'original'))
    }, 3600)

    return () => window.clearInterval(timer)
  }, [])

  useEffect(() => {
    const elements = document.querySelectorAll<HTMLElement>('[data-reveal="true"]')

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.visible)
          }
        })
      },
      { threshold: 0.12 },
    )

    elements.forEach((element) => observer.observe(element))

    return () => observer.disconnect()
  }, [])

  const handlePrimaryCtaClick = (
    event: MouseEvent<HTMLAnchorElement>,
    source: LandingCtaSource,
  ) => {
    if (isReaderLandingPlaceholderHref(cta.href)) {
      event.preventDefault()
    }

    trackLandingCtaClick(cta, source)
  }

  const handlePreviewCtaButtonClick = () => {
    trackLandingCtaClick(cta, 'preview_cta')

    if (isReaderLandingPlaceholderHref(cta.href)) {
      return
    }

    window.location.href = cta.href
  }

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroOrbs} aria-hidden="true">
          <span className={`${styles.heroOrb} ${styles.heroOrbLarge}`} />
          <span className={`${styles.heroOrb} ${styles.heroOrbMedium}`} />
          <span className={`${styles.heroOrb} ${styles.heroOrbSmall}`} />
          <span className={`${styles.heroSpark} ${styles.heroSparkOne}`} />
          <span className={`${styles.heroSpark} ${styles.heroSparkTwo}`} />
          <span className={`${styles.heroSpark} ${styles.heroSparkThree}`} />
        </div>

        <div className={styles.heroInner}>
          <div className={styles.heroLayout}>
            <div className={`${styles.heroCopy} ${styles.reveal}`} data-reveal="true">
              <div className={styles.heroTagList}>
                {heroSpeechBubbles.map((bubble) => (
                  <span key={bubble} className={styles.heroTag}>
                    {bubble}
                  </span>
                ))}
              </div>

              <h1 className={styles.heroTitle}>
                {heroContent.titleLine1}
                <br />
                <span className={styles.heroTitleAccent}>{heroContent.titleAccent}</span>
                <br />
                {heroContent.titleLine3}
              </h1>
              <p className={styles.heroSubtitle}>
                {heroContent.subtitle}
                <br />
                {heroContent.subtitleBr}
              </p>

              <div className={styles.ctaRow}>
                <a
                  href={cta.href}
                  className={styles.primaryCta}
                  onClick={(event) => handlePrimaryCtaClick(event, 'hero_cta')}
                >
                  <span>{cta.label}</span>
                  <ArrowRight size={18} />
                </a>
              </div>
              <a
                href="#flow"
                className={styles.secondaryLinkCta}
                onClick={(event) => scrollToAnchor(event, '#flow')}
              >
                {heroContent.secondaryCta}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.section} id="flow">
        <div className={styles.sectionInner}>
          <SectionLabel text={flowSectionContent.label} className={styles.reveal} />
          <h2 className={`${styles.sectionTitle} ${styles.sectionTitleCentered} ${styles.reveal}`} data-reveal="true">
            {flowSectionContent.title}
          </h2>
          <p className={`${styles.sectionDesc} ${styles.sectionDescCentered} ${styles.reveal}`} data-reveal="true">
            {flowSectionContent.description}
          </p>

          <div className={styles.flowGrid}>
            <article className={`${styles.flowCard} ${styles.reveal}`} data-reveal="true">
              <div className={styles.flowCardHead}>
                <span className={styles.flowStep}>{preferenceScreen.step}</span>
                <span className={styles.flowLabel}>{preferenceScreen.label}</span>
              </div>
              <h3 className={styles.flowTitle}>{preferenceScreen.title}</h3>
              <p className={styles.flowDescription}>{preferenceScreen.description}</p>

              <div className={styles.preferenceMockWrap}>
                <div className={legacyPreviewStyles.showcaseUiFrame}>
                  <div className={legacyPreviewStyles.pairingTabs}>
                    <button
                      type="button"
                      className={`${legacyPreviewStyles.pairingTab} ${pairingMode === 'original' ? legacyPreviewStyles.pairingTabActive : ''}`}
                      onClick={() => setPairingMode('original')}
                    >
                      원작 커플링
                    </button>
                    <button
                      type="button"
                      className={`${legacyPreviewStyles.pairingTab} ${pairingMode === 'converted' ? legacyPreviewStyles.pairingTabActive : ''}`}
                      onClick={() => setPairingMode('converted')}
                    >
                      변환된 커플링
                    </button>
                  </div>

                  <div
                    className={`${legacyPreviewStyles.pairingLetter} ${legacyPreviewStyles.pairingLetterSwitch}`}
                    key={pairingMode}
                    data-mode={pairingMode}
                  >
                    <p className={legacyPreviewStyles.pairingLine}>
                      <span className={`${legacyPreviewStyles.pairingChangedName} ${legacyPreviewStyles.pairingChangedFx}`}>
                        {pairingMode === 'original' ? '지훈' : '내 이름'}
                      </span>
                      은 카페 창가에 멈춰 섰다.
                    </p>
                    <p className={legacyPreviewStyles.pairingLine}>
                      그리고
                      <span className={`${legacyPreviewStyles.pairingChangedName} ${legacyPreviewStyles.pairingChangedFx}`}>
                        {pairingMode === 'original' ? ' 현준 ' : ' 최애 '}
                      </span>
                      과 눈이 마주친 순간, 심장이 빠르게 뛰기 시작했다.
                    </p>
                    <p className={legacyPreviewStyles.pairingLine}>
                      그가 웃으며 건넨 &quot;오늘도 왔네&quot;라는 말 한마디에
                      <span className={`${legacyPreviewStyles.pairingChangedName} ${legacyPreviewStyles.pairingChangedFx}`}>
                        {pairingMode === 'original' ? ' 지훈 ' : ' 내 이름 '}
                      </span>
                      은 숨을 고르지 못했다.
                    </p>
                    <p className={legacyPreviewStyles.pairingLine}>
                      잠깐 스친 손끝의 온기 때문에
                      <span className={`${legacyPreviewStyles.pairingChangedName} ${legacyPreviewStyles.pairingChangedFx}`}>
                        {pairingMode === 'original' ? ' 지훈 ' : ' 내 이름 '}
                      </span>
                      은 한참 동안 말을 잇지 못했다.
                    </p>
                    <p className={legacyPreviewStyles.pairingLine}>
                      같은 장면이어도
                      <span className={`${legacyPreviewStyles.pairingChangedName} ${legacyPreviewStyles.pairingChangedFx}`}>
                        {pairingMode === 'original' ? ' 현준 ' : ' 최애 '}
                      </span>
                      을 향한 감정선이 달라지면 이야기가 완전히 새롭게 읽힌다.
                    </p>
                  </div>
                </div>
              </div>
            </article>

            <article className={`${styles.flowCard} ${styles.flowCardFeatured} ${styles.reveal}`} data-reveal="true">
              <div className={styles.flowCardHead}>
                <span className={styles.flowStep}>{feedScreen.step}</span>
                <span className={styles.flowLabel}>{feedScreen.label}</span>
              </div>
              <h3 className={styles.flowTitle}>{feedScreen.title}</h3>
              <p className={styles.flowDescription}>{feedScreen.description}</p>

              <div className={`${legacyPreviewStyles.showcaseUiFrame} ${styles.flowPreviewFrame}`}>
                <div className={legacyPreviewStyles.previewTabs}>
                  <button
                    type="button"
                    className={`${legacyPreviewStyles.previewTabButton} ${previewMode === 'thread' ? legacyPreviewStyles.previewTabButtonActive : ''}`}
                    onClick={() => setPreviewMode('thread')}
                  >
                    {feedScreen.tabs.thread.label}
                  </button>
                  <button
                    type="button"
                    className={`${legacyPreviewStyles.previewTabButton} ${previewMode === 'chat' ? legacyPreviewStyles.previewTabButtonActive : ''}`}
                    onClick={() => setPreviewMode('chat')}
                  >
                    {feedScreen.tabs.chat.label}
                  </button>
                </div>

                <div
                  className={`${legacyPreviewStyles.switchUi} ${previewMode === 'thread' ? legacyPreviewStyles.uiActive : legacyPreviewStyles.uiInactive}`}
                >
                  <div className={legacyPreviewStyles.threadFeed}>
                    <div className={legacyPreviewStyles.threadItem}>
                      <div className={legacyPreviewStyles.threadAvatar}>🐿️</div>
                      <div className={legacyPreviewStyles.threadMeta}>
                        <div className={legacyPreviewStyles.threadNickname}>{feedScreen.tabs.thread.nickname}</div>
                        <div className={legacyPreviewStyles.threadContent}>{feedScreen.tabs.thread.content1}</div>
                        <div className={legacyPreviewStyles.threadActions}>
                          <span className={legacyPreviewStyles.threadActionItem}>♡ 24</span>
                          <span className={legacyPreviewStyles.threadActionItem}>💬 7</span>
                        </div>
                      </div>
                    </div>
                    <div className={legacyPreviewStyles.threadItem}>
                      <div className={legacyPreviewStyles.threadAvatar}>🐿️</div>
                      <div className={legacyPreviewStyles.threadMeta}>
                        <div className={legacyPreviewStyles.threadNickname}>{feedScreen.tabs.thread.nickname}</div>
                        <div className={legacyPreviewStyles.threadContent}>{feedScreen.tabs.thread.content2}</div>
                        <div className={legacyPreviewStyles.threadActions}>
                          <span className={legacyPreviewStyles.threadActionItem}>♡ 31</span>
                          <span className={legacyPreviewStyles.threadActionItem}>💬 9</span>
                        </div>
                      </div>
                    </div>
                    <div className={legacyPreviewStyles.threadCtaRow}>
                      <button
                        type="button"
                        className={legacyPreviewStyles.threadCtaButton}
                        onClick={handlePreviewCtaButtonClick}
                      >
                        {feedScreen.tabs.thread.cta} →
                      </button>
                    </div>
                  </div>
                </div>

                <div
                  className={`${legacyPreviewStyles.switchUi} ${previewMode === 'chat' ? legacyPreviewStyles.uiActive : legacyPreviewStyles.uiInactive}`}
                >
                  <div className={legacyPreviewStyles.chatMessage}>
                    <div className={legacyPreviewStyles.chatMetaRow}>
                      <span className={legacyPreviewStyles.chatAvatar}>🐰</span>
                      <span className={legacyPreviewStyles.chatNickname}>{feedScreen.tabs.chat.leftName}</span>
                    </div>
                    <div className={legacyPreviewStyles.chatBubbleLeft}>{feedScreen.tabs.chat.bubble1}</div>
                  </div>
                  <div className={`${legacyPreviewStyles.chatMessage} ${legacyPreviewStyles.chatMessageRight}`}>
                    <div className={`${legacyPreviewStyles.chatMetaRow} ${legacyPreviewStyles.chatMetaRowRight}`}>
                      <span className={legacyPreviewStyles.chatNickname}>{feedScreen.tabs.chat.rightName}</span>
                      <span className={legacyPreviewStyles.chatAvatar}>🐢</span>
                    </div>
                    <div className={legacyPreviewStyles.chatBubbleRight}>{feedScreen.tabs.chat.bubble2}</div>
                  </div>
                  <div className={legacyPreviewStyles.chatMessage}>
                    <div className={legacyPreviewStyles.chatMetaRow}>
                      <span className={legacyPreviewStyles.chatAvatar}>🐰</span>
                      <span className={legacyPreviewStyles.chatNickname}>{feedScreen.tabs.chat.leftName}</span>
                    </div>
                    <div className={legacyPreviewStyles.chatBubbleLeft}>{feedScreen.tabs.chat.bubble3}</div>
                  </div>
                  <button
                    type="button"
                    className={legacyPreviewStyles.chatCtaButton}
                    onClick={handlePreviewCtaButtonClick}
                  >
                    {feedScreen.tabs.chat.cta} →
                  </button>
                </div>
              </div>

            </article>

          </div>
        </div>
      </section>

      <section className={`${styles.section} ${styles.faqSection}`} id="faq">
        <div className={styles.sectionInner}>
          <SectionLabel text={faqSectionContent.label} className={styles.reveal} />
          <h2 className={`${styles.sectionTitle} ${styles.sectionTitleCentered} ${styles.reveal}`} data-reveal="true">
            {faqSectionContent.title}
          </h2>
          <p className={`${styles.sectionDesc} ${styles.sectionDescCentered} ${styles.reveal}`} data-reveal="true">
            {faqSectionContent.description}
          </p>

          <div className={styles.faqList}>
            {faqItems.map((item, index) => (
              <article
                key={item.question}
                className={`${styles.faqItem} ${styles.reveal}`}
                data-reveal="true"
                style={{ transitionDelay: `${index * 0.08}s` }}
              >
                <h3 className={styles.faqQuestion}>{item.question}</h3>
                <div className={styles.faqAnswer}>
                  {item.answers.map((answer) => (
                    <p key={`${item.question}-${answer}`} className={styles.faqAnswerLine}>
                      {answer}
                    </p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.finalSection} id="cta">
        <div className={styles.sectionInner}>
          <div className={styles.finalCopy}>
            <SectionLabel text={finalCtaContent.label} />
            <h2 className={styles.finalTitle}>{finalCtaContent.title}</h2>
            <p className={styles.finalDescription}>{finalCtaContent.description}</p>
          </div>

          <div className={styles.finalButtonRow}>
            <a
              href={cta.href}
              className={styles.primaryCta}
              onClick={(event) => handlePrimaryCtaClick(event, 'final_cta')}
            >
              <span>{cta.label}</span>
              <ArrowRight size={18} />
            </a>
            <a
              href="#flow"
              className={styles.secondaryLinkCta}
              onClick={(event) => scrollToAnchor(event, '#flow')}
            >
              {finalCtaContent.secondaryCta}
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
